/**
 * [Note] Do not use this mixin for other purpose. This is where I move all the code of FormBuilder to keep easy to:
 *  - Structuring
 *  - Refactoring
 *  - ...
 *  This file will be handled all the Event (mostly listening) from the children to update the big `formData`
 *  @author Phat Tran <phattranminh96@gmail.com>
 */
import {EVENT_CONSTANTS} from "../../configs/events";
import {HELPER} from "../../libraries/helper";
import {SUPPORTED_LANGUAGES, langSuffix} from "../../utils/languageHelper";
import {graftSection} from "../../libraries/section-copy";
import {describeCopyReport} from "../../libraries/section-copy-describe";


const FORM_BUILDER_EVENT_HANDLER = {
    methods: {
        /**
         * Do mapping for section after row added
         * @param sectionId
         * @param rowId
         */
        sectionAndRowMapping(sectionId, rowId) {
            // push it into the section Rows...
            // I can ensure that sectionId is exists to be retrieved
            this.formData.sections[sectionId].rows.push(rowId)
        },

        /**
         * Push-up section (SORT)
         * @param sectionObj
         * @param {Number} type
         *  - 0 => Up
         *  - 1 => Down
         */
        sectionPushedUp(sectionObj, type = 0) {
            if (
                // sort == 0 and push up => stop
                (sectionObj.sortOrder <= 1 && type === 0)
                ||
                // sort == total_sections and push down => stop
                (sectionObj.sortOrder === this.sortedSections.length && type === 1)
            ) {
                return
            }

            // old sort order to exchange with the upper section
            let postSortOrder = sectionObj.sortOrder;

            // pick section from sort order - Sort Order is unique
            let preSectionOrder = (type === 0) ? (postSortOrder - 1) : (postSortOrder + 1)
            let preSection = HELPER.find(this.formData.sections, "sortOrder", preSectionOrder)

            // swap now
            this.$set(this.formData.sections[sectionObj.uniqueId], 'sortOrder', preSectionOrder)
            this.$set(this.formData.sections[preSection.uniqueId], 'sortOrder', postSortOrder)

            // Sort Again After Swapped Order
            this.doSortSection()
        },

        /**
         * Delete a section
         * @param sectionId
         */
        sectionDelete(sectionId) {
            // validate input
            if (!this.formData.sections[sectionId]) {
                return
            }

            // need to delete all the related control & row
            let sectionObj = this.formData.sections[sectionId];
            sectionObj.rows.forEach(rowId => {

                // delete inner control of the rows
                let rowItem = this.formData.rows[rowId]
                rowItem.controls.forEach(controlId => {
                    // delete control by ID :D
                    this.$delete(this.formData.controls, controlId)
                });

                // delete this rows.
                this.$delete(this.formData.rows, rowItem.uniqueId)
            })

            // delete ($delete to reactive)
            this.$delete(this.formData.sections, sectionId)

            // Sort Again After Deleted
            this.doSortSection()

            // re-index sortOrder
            // thankfully this still keep reference... :D
            let index = 1;
            for (let sectionObj of this.sortedSections) {
                sectionObj.sortOrder = index++;
            }
        },

        /**
         * Update data for section
         * @param sectionObj
         */
        sectionUpdate(sectionObj) {
            let sectionId = sectionObj.uniqueId

            // validate input
            if (!this.formData.sections.hasOwnProperty(sectionId)) {
                return
            }

            // update by using the extend . best way
            this.formData.sections[sectionId] = Object.assign(this.formData.sections[sectionId], sectionObj)
        },

        /**
         * Added new row
         * @param rowObject
         */
        rowNewAdded(rowObject) {
            // $set => reactive
            this.$set(this.formData.rows, rowObject.uniqueId, rowObject)
        },

        /**
         * Delete a specific row in the section
         * @param {string} rowId rowId that need to be delete
         * @param {string} sectionId row's current sectionId
         */
        rowDelete(rowId, sectionId) {
            // first, delete row in section
            const indexInSection = HELPER.findIndex(this.formData.sections[sectionId].rows, undefined, rowId)
            this.formData.sections[sectionId].rows.splice(indexInSection, 1)

            // second, remove row in the big rows
            this.$delete(this.formData.rows, rowId)

            // lastly, final init to which-ever components listen to the event after deleted
            this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.ROW.DELETED, rowId, sectionId)
        },


        /**
         * Added new control to a section
         * @param {string} parentId
         * @param {Object} controlObj
         */
        controlNewAdded(parentId, controlObj) {
            // add into big list
            this.$set(this.formData.controls, controlObj.uniqueId, controlObj)

            // get type of the parent (section / row)
            const type = this.formData.sections.hasOwnProperty(parentId)
                ? 'section'
                : 'row';
            const controlUniqueId = controlObj.uniqueId

            // add controlID to section / row
            if (type === 'section') {
                this.formData.sections[parentId].controls.push(controlUniqueId)
            } else {
                this.formData.rows[parentId].controls.push(controlUniqueId)
            }
        },

        /**
         * Delete a control from section/row
         * @param {string} parentId - Might be SectionId, might be RowId
         * @param {string} controlId - LOL
         * @afterHandled Emit an event to notify the deletion is complete
         */
        controlDeletion(parentId, controlId) {
            const type = this.formData.sections.hasOwnProperty(parentId)
                ? 'section'
                : 'row';

            // FIRST: We delete the relationship in section/row
            if (type === 'section') {
                // find index and delete in section-controls
                let indexInSection = HELPER.findIndex(this.formData.sections[parentId].controls, undefined, controlId)
                this.formData.sections[parentId].controls.splice(indexInSection, 1)
            } else {
                // find index and delete in row-controls
                let indexInRow = HELPER.findIndex(this.formData.rows[parentId].controls, undefined, controlId)
                this.formData.rows[parentId].controls.splice(indexInRow, 1)
            }

            // SECOND: We delete the control object in `controls`
            this.$delete(this.formData.controls, controlId)

            // LAST: Emit DELETED (might be some component will register this??)
            this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.CONTROL.DELETED, parentId, controlId)
        },

        /**
         * Produce a fresh, form-unique control `name` for a duplicated control.
         * `name` is the submission-data key; there is no uniqueness enforcement elsewhere, so a
         * naive duplicate would make two controls share one key (one silently overwrites the other).
         * - empty / blank source name -> '' (renderer falls back to the new uniqueId, which is unique)
         * - a back-filled "control-…" id used as a name -> '' (don't produce "control-uuid Copy")
         * - otherwise -> "<name> Copy", "<name> Copy 2", … until unused
         * @param {String} sourceName
         * @returns {String}
         */
        generateUniqueControlName(sourceName) {
            if (!sourceName || String(sourceName).trim() === '') return ''
            if (String(sourceName).indexOf('control-') === 0) return ''

            const existing = new Set(
                Object.values(this.formData.controls)
                    .map(c => c && c.name)
                    .filter(n => n && String(n).trim() !== '')
            )

            let candidate = `${sourceName} Copy`
            if (!existing.has(candidate)) return candidate

            let i = 2
            while (existing.has(`${sourceName} Copy ${i}`)) i++
            return `${sourceName} Copy ${i}`
        },

        /**
         * Deep-clone a single control for duplication, register it into formData.controls, and return
         * the new control object. Clears the Open Dental mapping and assigns a fresh unique name so the
         * copy never collides with the original on the same patient column / submission key.
         * `$set` is done here (before any subsequent clone) so generateUniqueControlName sees prior copies.
         * @param {Object} source
         * @returns {Object} the registered clone
         */
        _registerClonedControl(source) {
            const clone = HELPER.cloneDeep(source)
            clone.uniqueId = "control-" + HELPER.getUUIDv4()
            clone.createdAt = Date.now() // reuses the "newly-added" highlight in ControlView
            clone.teamioODColumn = ''
            clone.name = this.generateUniqueControlName(source.name)

            this.$set(this.formData.controls, clone.uniqueId, clone)
            return clone
        },

        /**
         * Duplicate a single control and insert the copy directly AFTER the original in the same
         * section/row. Mirrors controlDeletion's container detection, but inserts instead of removes.
         * @param {String} parentId - section or row id
         * @param {String} controlId - id of the control to clone
         */
        controlDuplication(parentId, controlId) {
            const source = this.formData.controls[controlId]
            if (!source) return

            const clone = this._registerClonedControl(source)

            // figure out the container (section vs row) the same way as add/delete
            const type = this.formData.sections.hasOwnProperty(parentId) ? 'section' : 'row'
            const arr = (type === 'section')
                ? this.formData.sections[parentId] && this.formData.sections[parentId].controls
                : this.formData.rows[parentId] && this.formData.rows[parentId].controls
            if (!Array.isArray(arr)) return

            const idx = HELPER.findIndex(arr, undefined, controlId)
            arr.splice(idx + 1, 0, clone.uniqueId) // idx === -1 -> insert at front (safe fallback)
        },

        /**
         * Duplicate an entire section (header + every control) and place the copy immediately after
         * the original. Every control (and, for tab sections, every row) gets a brand-new id so the
         * clone shares nothing with the source. Works for Normal/Toggleable (flat section.controls)
         * and Tab (section.rows -> row.controls). Table sections are disabled in the codebase.
         * @param {String} sectionId
         */
        sectionDuplication(sectionId) {
            const source = this.formData.sections[sectionId]
            if (!source) return

            const newSection = HELPER.cloneDeep(source)
            newSection.uniqueId = "section-" + HELPER.getUUIDv4()

            // clone one control id -> register the clone -> return its new id
            const cloneControlId = (oldControlId) => {
                const src = this.formData.controls[oldControlId]
                if (!src) return null
                return this._registerClonedControl(src).uniqueId
            }

            // Flat sections (Normal / Toggleable) store control ids directly on the section
            if (Array.isArray(newSection.controls) && newSection.controls.length) {
                newSection.controls = source.controls.map(cloneControlId).filter(Boolean)
            }

            // Tab sections store rows, each with its own controls
            if (Array.isArray(newSection.rows) && newSection.rows.length) {
                newSection.rows = source.rows.map(oldRowId => {
                    const srcRow = this.formData.rows[oldRowId]
                    if (!srcRow) return null
                    const row = HELPER.cloneDeep(srcRow)
                    row.uniqueId = "row-" + HELPER.getUUIDv4()
                    row.controls = Array.isArray(srcRow.controls)
                        ? srcRow.controls.map(cloneControlId).filter(Boolean)
                        : []
                    this.$set(this.formData.rows, row.uniqueId, row)
                    return row.uniqueId
                }).filter(Boolean)
            }

            // Place the clone right after the original; keep sortOrder unique & 1-based.
            // Shift existing later sections up by 1 BEFORE inserting the clone (which isn't in the map yet).
            const baseOrder = source.sortOrder
            Object.values(this.formData.sections).forEach(s => {
                if (s.sortOrder > baseOrder) s.sortOrder += 1
            })
            newSection.sortOrder = baseOrder + 1

            this.$set(this.formData.sections, newSection.uniqueId, newSection)

            // Re-sort so the new section renders in its place
            if (typeof this.doSortSection === 'function') {
                this.doSortSection()
            }
        },

        /**
         * Add Section -> "Copy a section from another form": graft ONE section of another form on this
         * site onto the END of this form (SectionCopyModal -> AddSectionControl -> here). graftSection
         * (libraries/section-copy.js, shared with teamio-admin) gives fresh ids, keeps names / Open
         * Dental mappings unless this form already uses them, and rewrites or drops rules that point
         * outside the section. Nothing is saved: the office reviews the toasts, then Save runs the usual
         * gates (EditForm / CreateForm _saveForm).
         * @param {Object} sourceConfig - the source form's parsed ConfigData
         * @param {String} sectionId - key of the section in sourceConfig.sections
         */
        sectionCopyFromForm(sourceConfig, sectionId) {
            // $formEvent is app-global and this mixin never $off's: a destroyed FormBuilder still
            // hears the event, and without this guard one pick would graft once per stale builder.
            if (this._isDestroyed) return

            let result
            try {
                result = graftSection(sourceConfig, sectionId, this.formData, { uuid: HELPER.getUUIDv4 })
            } catch (err) {
                console.error('[sectionCopyFromForm] could not copy the section:', err)
                if (this.$toast) this.$toast.error("That section couldn't be copied. Please try again.")
                return
            }

            const { config, report } = result
            const copied = report[0]

            if (!this.formData.controls) this.$set(this.formData, 'controls', {})
            if (!this.formData.rows) this.$set(this.formData, 'rows', {})

            // controls and rows first, so the section renders with every child in place
            copied.controlIds.forEach(id => this.$set(this.formData.controls, id, config.controls[id]))
            copied.rowIds.forEach(id => this.$set(this.formData.rows, id, config.rows[id]))

            // graftSection renumbers every section 1..n; carry the new numbers over
            Object.keys(this.formData.sections).forEach(key => {
                const next = config.sections[key]
                const current = this.formData.sections[key]
                if (next && current && current.sortOrder !== next.sortOrder) {
                    this.$set(current, 'sortOrder', next.sortOrder)
                }
            })
            this.$set(this.formData.sections, copied.sectionId, config.sections[copied.sectionId])

            if (typeof this.doSortSection === 'function') {
                this.doSortSection()
            }

            if (this.$toast) {
                const words = describeCopyReport(report, { config, sourceConfig })
                this.$toast.success(words.success)
                if (words.warnings.length) {
                    const shown = words.warnings.slice(0, 6)
                    const more = words.warnings.length - shown.length
                    this.$toast.warning(
                        'Check before you save: ' + shown.join(' ') + (more > 0 ? ` …and ${more} more.` : ''),
                        { timeout: 20000 }
                    )
                }
            }
        },

        /**
         * Relocate an existing control to another section / position. Pure MOVE: the control object in
         * `formData.controls` is untouched (same uniqueId, name, teamioODColumn, validations, visibility
         * rules) — only its id moves between container arrays. Mirrors controlDeletion's container
         * detection, but splices the id into the target instead of $deleting the object.
         *
         * The control is removed from the source FIRST, then the anchor is resolved against the (now
         * updated) target array, so repositioning WITHIN the same container stays correct (no off-by-one).
         *
         * @param {String} sourceParentId - current section or row id
         * @param {String} controlId      - id of the control to move
         * @param {String} targetParentId - destination section or row id
         * @param {('top'|'end'|{afterControlId:String})} anchor - where in the target to drop it
         */
        controlMove(sourceParentId, controlId, targetParentId, anchor) {
            if (!this.formData.controls[controlId]) return

            const containerControls = (parentId) =>
                this.formData.sections.hasOwnProperty(parentId)
                    ? (this.formData.sections[parentId] && this.formData.sections[parentId].controls)
                    : (this.formData.rows[parentId] && this.formData.rows[parentId].controls)

            const srcArr = containerControls(sourceParentId)
            const tgtArr = containerControls(targetParentId)
            if (!Array.isArray(srcArr) || !Array.isArray(tgtArr)) return

            // remove from source FIRST so same-container anchor indices stay valid
            const srcIdx = HELPER.findIndex(srcArr, undefined, controlId)
            if (srcIdx === -1) return
            srcArr.splice(srcIdx, 1)

            // resolve insert index from the anchor
            let insertIdx
            if (anchor === 'top') {
                insertIdx = 0
            } else if (anchor && anchor.afterControlId) {
                const afterIdx = HELPER.findIndex(tgtArr, undefined, anchor.afterControlId)
                insertIdx = (afterIdx === -1) ? tgtArr.length : afterIdx + 1
            } else {
                // 'end' or unknown
                insertIdx = tgtArr.length
            }
            tgtArr.splice(insertIdx, 0, controlId)

            // reuse the "newly-added" glow so the author sees where it landed (re-mounts on cross-section move)
            this.$set(this.formData.controls[controlId], 'createdAt', Date.now())
        },

        /**
         * Update a control
         * @param {String} controlId
         * @param {Object} controlData
         */
        controlUpdated(controlId, controlData) {
            // validate input
            if (!this.formData.controls.hasOwnProperty(controlId)) {
                return
            }

            // update by using the extend . best way
            this.formData.controls[controlId] = Object.assign(this.formData.controls[controlId], controlData)
        },

        /**
         * Handle translation complete event - merge translated fields into form data.
         * Dynamically merges any field ending with a known language suffix (Es, Ru, etc.)
         * @param {Object} translatedConfig - Object containing translated sections, controls
         */
        handleTranslationComplete(translatedConfig) {
            if (!translatedConfig) return;

            // Build list of known suffixes from supported languages
            const knownSuffixes = SUPPORTED_LANGUAGES
                .filter(l => l !== 'en')
                .map(l => langSuffix(l));

            // Helper: check if a field key is a translation field
            const isTranslationField = (key) => knownSuffixes.some(s => key.endsWith(s));

            // Merge translated sections
            if (translatedConfig.sections) {
                for (const [sectionId, sectionData] of Object.entries(translatedConfig.sections)) {
                    if (this.formData.sections[sectionId]) {
                        for (const [key, value] of Object.entries(sectionData)) {
                            if (isTranslationField(key)) {
                                this.$set(this.formData.sections[sectionId], key, value);
                            }
                        }
                    }
                }
            }

            // Merge translated controls
            if (translatedConfig.controls) {
                for (const [controlId, controlData] of Object.entries(translatedConfig.controls)) {
                    if (this.formData.controls[controlId]) {
                        for (const [key, value] of Object.entries(controlData)) {
                            if (isTranslationField(key)) {
                                this.$set(this.formData.controls[controlId], key, value);
                            }
                        }
                    }
                }
            }

            // Merge formConfig translation fields
            if (translatedConfig.formConfig && this.formData.formConfig) {
                for (const [key, value] of Object.entries(translatedConfig.formConfig)) {
                    if (isTranslationField(key)) {
                        this.$set(this.formData.formConfig, key, value);
                    }
                }
            }

            console.log('Translation merged into form data');

            // Emit event to trigger form save in EditForm
            this.$formEvent.$emit('saveFormAfterTranslation');
        }
    },

    created() {
        // section events
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.ADDED_ROW, this.sectionAndRowMapping)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.PUSH, this.sectionPushedUp)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.DELETE, this.sectionDelete)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.UPDATE, this.sectionUpdate)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.DUPLICATE, this.sectionDuplication)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.SECTION.COPY_FROM_FORM, this.sectionCopyFromForm)

        // row events
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.ROW.CREATE, this.rowNewAdded)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.ROW.DELETE, this.rowDelete)

        // control events
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.CONTROL.CREATE, this.controlNewAdded)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.CONTROL.DELETE, this.controlDeletion)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.CONTROL.DUPLICATE, this.controlDuplication)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.CONTROL.MOVE, this.controlMove)
        this.$formEvent.$on(EVENT_CONSTANTS.BUILDER.CONTROL.UPDATE, this.controlUpdated)

        // translation events
        this.$formEvent.$on('translationComplete', this.handleTranslationComplete)
    }
}

export {
    FORM_BUILDER_EVENT_HANDLER
}